import { useMemo, useState } from 'react';
import { BarChart } from '../../charts/BarChart';
import { LineChart } from '../../charts/LineChart';
import { RankedBars } from '../../charts/RankedBars';
import { ChartCard, Segmented, Stat } from '../../components/data';
import { ErrorBanner, Loading } from '../../components/ui';
import { num, pct, secs, shortDate, snakeLabel, weekdayLabel } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { CallMetricRow, CallsData } from './adminTypes';
import { DeltaStat, RegisterEmpty, RegisterPanel, WindowPicker, formatIsoShort, type RegisterSpec } from './adminUi';

const QUEUE = ['service_desk', 'noc', 'sales', 'billing'] as const;

const WINDOWS = [
  { id: '30', label: '30 days' },
  { id: '90', label: '90 days' },
  { id: '365', label: '12 months' },
];

/** The service-level convention the standalone app measured against. Not stored
 *  per row, because it is a target rather than an observation — a measure that
 *  wants its own target belongs on the scorecard, where targets live. */
const SERVICE_LEVEL_TARGET = 80;

/**
 * Inbound call performance.
 *
 * THE ONE REGISTER HERE NOBODY FILLS IN BY HAND, and the page says so rather
 * than implying somebody forgot. A hundred and eighty days across four queues is
 * an export from the phone system, not typing, and no such feed exists into this
 * database yet. The table came across anyway because it is a plain measurement
 * table keyed on date and queue, needs no dimension this database cannot supply,
 * and a monthly paste is a realistic way to load it — see the exception noted at
 * the end of migration 048. If it is still empty in six months, that is the
 * argument for dropping it rather than for inventing rows.
 *
 * Every percentage here is VOLUME-WEIGHTED, computed in SQL. A quiet Sunday
 * answering four of four calls is not worth the same as a Monday answering 380
 * of 400, and averaging the two daily percentages says it is. That is why
 * `coo.CallMetric` stores counts rather than a monthly rate.
 */
export function AdminCalls({ canWrite, onChanged }: { canWrite: boolean; onChanged: () => void }) {
  const [window, setWindow] = useState(30);
  const [queue, setQueue] = useState<'all' | (typeof QUEUE)[number]>('all');

  const analytics = useResource<CallsData>(
    `/admin/analytics/calls?days=${window}${queue === 'all' ? '' : `&queue=${queue}`}`,
  );

  const spec = useMemo<RegisterSpec<CallMetricRow>>(
    () => ({
      resource: 'call-metrics',
      title: 'Daily call data',
      singular: 'Day',
      emptyTitle: 'Nothing to list yet',
      emptyBody:
        'Rows added here appear in the analysis above. One row per queue per day: the counts, then the four averages. The date and queue together are unique, so re-importing a month replaces it rather than doubling it.',
      searchHint: 'Not searchable — filter by queue',
      idOf: (row) => row.CallMetricId,
      labelOf: (row) => `${formatIsoShort(row.MetricDate)} — ${snakeLabel(row.Queue)}`,
      filters: [{ param: 'queue', label: 'Queue', options: QUEUE }],
      columns: [
        { key: 'MetricDate', label: 'Date', render: (row) => formatIsoShort(row.MetricDate) },
        { key: 'Queue', label: 'Queue', render: (row) => snakeLabel(row.Queue) },
        { key: 'CallsOffered', label: 'Offered', align: 'right' },
        { key: 'CallsAnswered', label: 'Answered', align: 'right' },
        {
          key: 'CallsAbandoned',
          label: 'Abandoned',
          align: 'right',
          render: (row) => (
            <span className={row.CallsAbandoned > 0 ? 'admin-warn-text' : undefined}>
              {row.CallsAbandoned}
            </span>
          ),
        },
        {
          key: 'ServiceLevelPercent',
          label: 'Service level',
          align: 'right',
          render: (row) => pct(row.ServiceLevelPercent, 1),
        },
        {
          key: 'AvgSpeedAnswerSec',
          label: 'Speed to answer',
          align: 'right',
          render: (row) => secs(row.AvgSpeedAnswerSec),
        },
        {
          key: 'AvgHandleTimeSec',
          label: 'Handle time',
          align: 'right',
          render: (row) => secs(row.AvgHandleTimeSec),
        },
        {
          key: 'FirstCallResPercent',
          label: 'FCR',
          align: 'right',
          render: (row) => pct(row.FirstCallResPercent, 1),
        },
        {
          key: 'CsatScore',
          label: 'CSAT',
          align: 'right',
          /* Null when no survey came back that day, which is a different and
             more honest statement than a score of zero. */
          render: (row) =>
            row.CsatScore === null ? <span className="sub">—</span> : `${num(row.CsatScore, 2)}/5`,
        },
      ],
      fields: [
        { name: 'metricDate', label: 'Date', kind: 'date' },
        { name: 'queue', label: 'Queue', kind: 'select', options: QUEUE },
        {
          name: 'callsOffered',
          label: 'Calls offered',
          kind: 'int',
          hint: 'Answered plus abandoned cannot exceed this — the database enforces it too.',
        },
        { name: 'callsAnswered', label: 'Calls answered', kind: 'int' },
        { name: 'callsAbandoned', label: 'Calls abandoned', kind: 'int' },
        { name: 'voicemails', label: 'Voicemails', kind: 'int' },
        { name: 'afterHoursCalls', label: 'After-hours calls', kind: 'int' },
        { name: 'avgSpeedAnswerSec', label: 'Avg speed to answer (s)', kind: 'decimal' },
        { name: 'avgHandleTimeSec', label: 'Avg handle time (s)', kind: 'decimal' },
        { name: 'longestWaitSec', label: 'Longest wait (s)', kind: 'decimal' },
        { name: 'serviceLevelPercent', label: 'Service level %', kind: 'percent' },
        { name: 'firstCallResPercent', label: 'First-call resolution %', kind: 'percent' },
        {
          name: 'csatScore',
          label: 'CSAT (1–5)',
          kind: 'decimal',
          hint: 'Leave blank when no survey came back — a blank is excluded from the average, a zero is not.',
        },
      ],
      blank: {
        metricDate: '',
        queue: 'service_desk',
        callsOffered: 0,
        callsAnswered: 0,
        callsAbandoned: 0,
        voicemails: 0,
        afterHoursCalls: 0,
        avgSpeedAnswerSec: 0,
        avgHandleTimeSec: 0,
        longestWaitSec: 0,
        serviceLevelPercent: 0,
        firstCallResPercent: 0,
        csatScore: '',
      },
      toForm: (row) => ({
        metricDate: row.MetricDate?.slice(0, 10) ?? '',
        queue: row.Queue,
        callsOffered: row.CallsOffered,
        callsAnswered: row.CallsAnswered,
        callsAbandoned: row.CallsAbandoned,
        voicemails: row.Voicemails,
        afterHoursCalls: row.AfterHoursCalls,
        avgSpeedAnswerSec: row.AvgSpeedAnswerSec,
        avgHandleTimeSec: row.AvgHandleTimeSec,
        longestWaitSec: row.LongestWaitSec,
        serviceLevelPercent: row.ServiceLevelPercent,
        firstCallResPercent: row.FirstCallResPercent,
        csatScore: row.CsatScore ?? '',
      }),
    }),
    [],
  );

  const reload = () => {
    analytics.reload();
    onChanged();
  };

  const d = analytics.data;

  return (
    <div className="admin-stack">
      {analytics.error && <ErrorBanner message={analytics.error} />}
      {!d && analytics.loading && <Loading label="Loading call data" />}

      {/* Nothing loaded at all is a different page from nothing in this window —
          the first needs an import, the second needs a wider range. */}
      {d && d.coverage.rows === 0 && (
        <RegisterEmpty title="No phone data has been loaded">
          <p>
            This is the one register in the Admin module that expects an import rather than typing.
            The shape it wants is one row per queue per day, carrying the counts the phone system
            already reports — offered, answered, abandoned, voicemails, after-hours — plus its four
            averages.
          </p>
          <p className="mt-2">
            Every percentage on this tab is recomputed from those counts and volume-weighted, so a
            month loaded a day at a time is measured the same as a month loaded at once, and a
            partial month is not misreported. The unique key is the date and the queue together,
            which makes re-importing a month idempotent instead of doubling it.
          </p>
        </RegisterEmpty>
      )}

      {d && d.coverage.rows > 0 && (
        <>
          <div className="row-between wrap">
            <p className="sub">
              {num(d.coverage.rows)} {d.coverage.rows === 1 ? 'day' : 'days'} loaded, from{' '}
              {formatIsoShort(d.coverage.firstDate)} to {formatIsoShort(d.coverage.lastDate)}. Every
              percentage below is volume-weighted.
            </p>
            <div className="row wrap">
              <Segmented
                label="Queue"
                value={queue}
                onChange={(next) => setQueue(next)}
                options={[
                  { id: 'all' as const, label: 'All queues' },
                  ...QUEUE.map((q) => ({ id: q, label: snakeLabel(q) })),
                ]}
              />
              <WindowPicker value={window} onChange={setWindow} options={WINDOWS} />
            </div>
          </div>

          <div className="grid grid-4">
            <DeltaStat
              label="Service level"
              value={d.current.serviceLevelPercent}
              previous={d.previous.serviceLevelPercent}
              format={(v) => pct(v)}
              betterWhen="higher"
              foot={`against a ${SERVICE_LEVEL_TARGET}% target`}
            />
            <DeltaStat
              label="Abandon rate"
              value={d.current.abandonRatePercent}
              previous={d.previous.abandonRatePercent}
              format={(v) => pct(v)}
              betterWhen="lower"
            />
            <DeltaStat
              label="Speed to answer"
              value={d.current.avgSpeedAnswerSec}
              previous={d.previous.avgSpeedAnswerSec}
              format={(v) => secs(v)}
              betterWhen="lower"
            />
            <DeltaStat
              label="First-call resolution"
              value={d.current.firstCallResPercent}
              previous={d.previous.firstCallResPercent}
              format={(v) => pct(v)}
              betterWhen="higher"
            />
          </div>

          <div className="grid grid-4">
            <Stat
              label="Calls offered"
              value={num(d.current.offered)}
              foot={`${num(d.current.answered)} answered, ${num(d.current.abandoned)} abandoned`}
            />
            <Stat
              label="Handle time"
              value={secs(d.current.avgHandleTimeSec)}
              foot="volume-weighted average"
            />
            <Stat
              label="Longest wait"
              value={secs(d.current.longestWaitSec)}
              /* The max across the window, not an average — one 22-minute wait is
                 a complaint whatever the mean says. */
              foot="worst single wait in the window"
            />
            <Stat
              label="CSAT"
              value={d.current.csatScore === null ? '—' : `${num(d.current.csatScore, 2)}/5`}
              foot={
                d.current.csatScore === null
                  ? 'no survey responses recorded'
                  : `${num(d.current.voicemails)} voicemails, ${num(d.current.afterHours)} after hours`
              }
            />
          </div>

          <ChartCard
            title="Service level over time"
            subtitle={`Daily, volume-weighted where a day spans several queues. The dashed line is the ${SERVICE_LEVEL_TARGET}% convention.`}
            table={{
              columns: [
                {
                  key: 'metricDate',
                  label: 'Date',
                  render: (row: CallsData['daily'][number]) => formatIsoShort(row.metricDate),
                },
                { key: 'callsOffered', label: 'Offered', align: 'right' },
                {
                  key: 'serviceLevelPercent',
                  label: 'Service level',
                  align: 'right',
                  render: (row: CallsData['daily'][number]) => pct(row.serviceLevelPercent),
                },
                {
                  key: 'avgSpeedAnswerSec',
                  label: 'Speed to answer',
                  align: 'right',
                  render: (row: CallsData['daily'][number]) => secs(row.avgSpeedAnswerSec),
                },
              ],
              rows: d.daily,
              rowKey: (row) => row.metricDate,
            }}
          >
            <LineChart
              data={d.daily}
              xKey="metricDate"
              series={[{ key: 'serviceLevelPercent', label: 'Service level', color: 'var(--series-1)' }]}
              target={{ value: SERVICE_LEVEL_TARGET, label: `Target ${SERVICE_LEVEL_TARGET}%` }}
              xFormat={shortDate}
              yFormat={(v) => `${Math.round(v)}%`}
              area
              height={240}
            />
          </ChartCard>

          <div className="admin-split">
            <ChartCard
              title="Answered and abandoned"
              subtitle="Daily volume, stacked to the calls offered."
              table={{
                columns: [
                  {
                    key: 'metricDate',
                    label: 'Date',
                    render: (row: CallsData['daily'][number]) => formatIsoShort(row.metricDate),
                  },
                  { key: 'callsAnswered', label: 'Answered', align: 'right' },
                  { key: 'callsAbandoned', label: 'Abandoned', align: 'right' },
                  { key: 'afterHoursCalls', label: 'After hours', align: 'right' },
                ],
                rows: d.daily,
                rowKey: (row) => row.metricDate,
              }}
            >
              <BarChart
                data={d.daily}
                xKey="metricDate"
                stacked
                series={[
                  { key: 'callsAnswered', label: 'Answered', color: 'var(--series-1)' },
                  /* Abandoned takes the status ladder rather than a categorical
                     slot: it is not a second category of call, it is the failure
                     case, and it should read as one. This is the one place the
                     series/status boundary is crossed deliberately — see the note
                     in styles/tokens.css. */
                  { key: 'callsAbandoned', label: 'Abandoned', color: 'var(--status-critical)' },
                ]}
                xFormat={shortDate}
                yFormat={(v) => num(v)}
                height={240}
              />
            </ChartCard>

            <ChartCard
              title="Speed to answer and handle time"
              subtitle="Both volume-weighted over the calls they were averages of."
              table={{
                columns: [
                  {
                    key: 'metricDate',
                    label: 'Date',
                    render: (row: CallsData['daily'][number]) => formatIsoShort(row.metricDate),
                  },
                  {
                    key: 'avgSpeedAnswerSec',
                    label: 'Speed to answer',
                    align: 'right',
                    render: (row: CallsData['daily'][number]) => secs(row.avgSpeedAnswerSec),
                  },
                  {
                    key: 'avgHandleTimeSec',
                    label: 'Handle time',
                    align: 'right',
                    render: (row: CallsData['daily'][number]) => secs(row.avgHandleTimeSec),
                  },
                ],
                rows: d.daily,
                rowKey: (row) => row.metricDate,
              }}
            >
              <LineChart
                data={d.daily}
                xKey="metricDate"
                series={[
                  { key: 'avgSpeedAnswerSec', label: 'Speed to answer', color: 'var(--series-1)' },
                  { key: 'avgHandleTimeSec', label: 'Handle time', color: 'var(--series-2)' },
                ]}
                xFormat={shortDate}
                yFormat={(v) => `${Math.round(v)}s`}
                zeroBased
                height={240}
              />
            </ChartCard>
          </div>

          <div className="admin-split">
            <ChartCard
              title="By queue"
              subtitle="Volume across every queue over the window — not filtered by the queue picker above, so the four stay comparable."
              table={{
                columns: [
                  {
                    key: 'queue',
                    label: 'Queue',
                    render: (row: CallsData['byQueue'][number]) => snakeLabel(row.queue),
                  },
                  { key: 'callsOffered', label: 'Offered', align: 'right' },
                  { key: 'callsAbandoned', label: 'Abandoned', align: 'right' },
                  {
                    key: 'serviceLevelPercent',
                    label: 'Service level',
                    align: 'right',
                    render: (row: CallsData['byQueue'][number]) => pct(row.serviceLevelPercent),
                  },
                  {
                    key: 'avgSpeedAnswerSec',
                    label: 'Speed to answer',
                    align: 'right',
                    render: (row: CallsData['byQueue'][number]) => secs(row.avgSpeedAnswerSec),
                  },
                ],
                rows: d.byQueue,
                rowKey: (row) => row.queue,
              }}
            >
              <RankedBars
                rows={d.byQueue.map((q) => ({
                  label: snakeLabel(q.queue),
                  value: q.callsOffered,
                  meta: `${pct(q.serviceLevelPercent)} service level`,
                  /* Coloured by whether the queue is meeting the convention —
                     volume alone does not say which queue is struggling. */
                  color:
                    q.serviceLevelPercent !== null && q.serviceLevelPercent < SERVICE_LEVEL_TARGET
                      ? 'var(--status-serious)'
                      : undefined,
                }))}
                format={(v) => num(v)}
                labelWidth={132}
              />
            </ChartCard>

            <ChartCard
              title="By day of week"
              subtitle="Average calls offered and abandoned, to show where the staffing pressure sits."
              table={{
                columns: [
                  {
                    key: 'weekday',
                    label: 'Day',
                    render: (row: CallsData['byWeekday'][number]) => weekdayLabel(row.weekday),
                  },
                  {
                    key: 'avgOffered',
                    label: 'Avg offered',
                    align: 'right',
                    render: (row: CallsData['byWeekday'][number]) => num(row.avgOffered, 1),
                  },
                  {
                    key: 'avgAbandoned',
                    label: 'Avg abandoned',
                    align: 'right',
                    render: (row: CallsData['byWeekday'][number]) => num(row.avgAbandoned, 1),
                  },
                ],
                rows: d.byWeekday,
                rowKey: (row) => String(row.weekday),
              }}
            >
              <BarChart
                data={d.byWeekday}
                xKey="weekday"
                series={[
                  { key: 'avgOffered', label: 'Offered', color: 'var(--series-1)' },
                  { key: 'avgAbandoned', label: 'Abandoned', color: 'var(--status-critical)' },
                ]}
                xFormat={(v) => weekdayLabel(Number(v))}
                yFormat={(v) => num(v, 0)}
                height={240}
              />
            </ChartCard>
          </div>
        </>
      )}

      <RegisterPanel spec={spec} canWrite={canWrite} onChanged={reload} />
    </div>
  );
}
